<template>
    <div class="container-fluid">
        <nav-header :navItems="[{ name: 'Discover', link: { name: 'Discover My Data' } }]"
            pretitle="aqfer Data Discovery" title="Discover My Data" />

        <!-- ── Mode switch: catalog-first vs. file-browser-first ────────────── -->
        <div class="dmd-mode-switch mb-3">
            <button type="button" class="dmd-mode-btn" :class="{ active: mode === 'catalog' }" @click="mode = 'catalog'">
                <i class="fa fa-database" aria-hidden="true"></i> Catalog First
            </button>
            <button type="button" class="dmd-mode-btn" :class="{ active: mode === 'scan' }" @click="mode = 'scan'">
                <i class="fa fa-folder-open" aria-hidden="true"></i> Scan S3 Files
            </button>
            <span class="dmd-mode-hint">
                {{ mode === 'catalog'
                    ? 'Fast, bulk — only sees data already registered in the Glue Catalog.'
                    : 'Scans S3 directly' }}
            </span>
        </div>

        <!-- ── Catalog-first toolbar ─────────────────────────────────────────── -->
        <div v-if="mode === 'catalog'" class="card shadow-sm border-0 mb-3 dmd-toolbar-card">
            <div class="card-body dmd-toolbar">

                <div class="dmd-pill" :class="{ 'dmd-pill--loading': credentialLoading }">
                    <i class="fa fa-key" aria-hidden="true"></i>
                    <select v-model="credentialFilter" :disabled="credentialLoading">
                        <option value="">Credential…</option>
                        <option v-for="c in credentialList" :key="c" :value="c">{{ c }}</option>
                    </select>
                    <span v-if="credentialLoading" class="spinner-border spinner-border-sm"></span>
                </div>

                <div class="dmd-pill" :class="{ 'dmd-pill--loading': databasesLoading }">
                    <i class="fa fa-database" aria-hidden="true"></i>
                    <select v-model="databaseFilter" :disabled="submitting || databasesLoading">
                        <option value="">All databases</option>
                        <option v-for="db in databaseList" :key="db" :value="db">{{ db }}</option>
                    </select>
                    <span v-if="databasesLoading" class="spinner-border spinner-border-sm"></span>
                </div>

                <div class="dmd-pill" :class="{ 'dmd-pill--loading': tablesLoading, 'dmd-pill--disabled': !databaseFilter }">
                    <i class="fa fa-table" aria-hidden="true"></i>
                    <select v-model="tableFilter" :disabled="submitting || !databaseFilter || tablesLoading">
                        <option value="">All tables</option>
                        <option v-for="t in tableList" :key="t" :value="t">{{ t }}</option>
                    </select>
                    <span v-if="tablesLoading" class="spinner-border spinner-border-sm"></span>
                </div>

                <i class="fa fa-circle-question dmd-hint-icon" aria-hidden="true"
                    :title="`Leave Database blank to scan every database for this client (capped at ${MAX_DATABASES_HINT} per scan — narrow by Database to go beyond that). Pick a Credential so any table you Inspect can be scanned in its exact S3 folder — the catalog alone doesn't carry that credential.`"></i>

                <div class="dmd-toolbar-spacer"></div>

                <button type="button" class="btn btn-primary dmd-scan-btn" :disabled="submitDisabled"
                    @click="scanCatalog">
                    <span v-if="submitting" class="spinner-border spinner-border-sm me-1" role="status" />
                    <i v-else class="fa fa-bolt me-1" aria-hidden="true"></i>
                    <span>{{ submitting ? 'Scanning…' : 'Scan Catalog' }}</span>
                </button>

            </div>

            <!-- Scope error -->
            <div v-if="catalogError" class="alert alert-warning small py-1 px-3 mx-3 mb-3">{{ catalogError }}</div>
        </div>

        <!-- ── Scan-S3-files toolbar ─────────────────────────────────────────── -->
        <div v-else class="card shadow-sm border-0 mb-3 dmd-toolbar-card">
            <div class="card-body dmd-toolbar">

                <div class="dmd-pill" :class="{ 'dmd-pill--loading': credentialLoading }">
                    <i class="fa fa-key" aria-hidden="true"></i>
                    <select v-model="credentialFilter" :disabled="credentialLoading">
                        <option value="">Credential…</option>
                        <option v-for="c in credentialList" :key="c" :value="c">{{ c }}</option>
                    </select>
                    <span v-if="credentialLoading" class="spinner-border spinner-border-sm"></span>
                </div>

                <div class="dmd-pill" :class="{ 'dmd-pill--loading': bucketsLoading, 'dmd-pill--disabled': !credentialFilter }">
                    <i class="fa fa-box-archive" aria-hidden="true"></i>
                    <select v-model="bucketFilter" :disabled="!credentialFilter || bucketsLoading">
                        <option value="">Bucket…</option>
                        <option v-for="b in bucketList" :key="b" :value="b">{{ b }}</option>
                    </select>
                    <span v-if="bucketsLoading" class="spinner-border spinner-border-sm"></span>
                </div>

                <div ref="subfolderPill" class="dmd-pill dmd-pill--folder"
                    :class="{ 'dmd-pill--disabled': !bucketFilter }">
                    <i class="fa fa-folder-tree" aria-hidden="true"></i>
                    <input v-model.trim="prefixFilter" type="text" placeholder="prefix/ (optional)"
                        :disabled="!bucketFilter" class="dmd-pill-input" @focus="closeSubfolderMenu" />
                    <button type="button" class="dmd-subfolder-toggle" :disabled="!bucketFilter || subfoldersLoading"
                        title="Browse subfolders" @click="toggleSubfolderMenu">
                        <span v-if="subfoldersLoading" class="spinner-border spinner-border-sm"></span>
                        <i v-else class="fa fa-chevron-down" aria-hidden="true"></i>
                    </button>

                    <div v-if="subfolderMenuOpen" class="dmd-subfolder-menu">
                        <button v-if="prefixFilter" type="button" class="dmd-subfolder-item dmd-subfolder-item--up"
                            @click="goUpSubfolder">
                            <i class="fa fa-arrow-up me-1" aria-hidden="true"></i> Up one level
                        </button>
                        <div v-if="subfolderError" class="dmd-subfolder-msg text-danger">{{ subfolderError }}</div>
                        <div v-else-if="subfoldersLoading" class="dmd-subfolder-msg text-muted">Loading…</div>
                        <div v-else-if="!subfolderOptions.length" class="dmd-subfolder-msg text-muted">
                            No subfolders here.
                        </div>
                        <button v-for="opt in subfolderOptions" :key="opt" type="button" class="dmd-subfolder-item"
                            @click="selectSubfolder(opt)">
                            <i class="fa fa-folder me-1" aria-hidden="true"></i>{{ opt.replace(/\/$/, '') }}
                        </button>
                    </div>
                </div>

                <div class="dmd-toolbar-spacer"></div>
            </div>

            <div class="px-3 pb-3">
                <textarea v-model="scanPrompt" class="form-control form-control-sm" rows="2"
                    placeholder="What are you looking for? e.g. explore ZCR files if available"></textarea>
                <div class="d-flex align-items-center justify-content-between mt-2 flex-wrap gap-2">
                    <div class="form-check mb-0">
                        <input id="dmd-enrich" v-model="enrichWithCatalog" class="form-check-input" type="checkbox" />
                        <label class="form-check-label small" for="dmd-enrich">
                            Cross-check against the catalog
                            <i class="fa fa-circle-question dmd-hint-icon" aria-hidden="true"
                                title="For each file found, checks whether it's registered in the Glue Catalog — if so, adds its Iceberg-managed status and a real ZCR lookup-index check. Unregistered files are unaffected."></i>
                        </label>
                    </div>
                    <button type="button" class="btn btn-primary dmd-scan-btn" :disabled="scanDisabled" @click="scanFiles">
                        <span v-if="submitting" class="spinner-border spinner-border-sm me-1" role="status" />
                        <i v-else class="fa fa-bolt me-1" aria-hidden="true"></i>
                        <span>{{ submitting ? 'Scanning…' : 'Discover' }}</span>
                    </button>
                </div>
            </div>

            <div v-if="catalogError" class="alert alert-warning small py-1 px-3 mx-3 mb-3">{{ catalogError }}</div>
        </div>

        <p class="text-muted mb-3 text-end" style="font-size:.7rem; opacity:.7">
            This tool uses AI, which can make mistakes. Verify important outputs before acting on them.
        </p>

        <!-- ── Results ────────────────────────────────────────────────────── -->
        <div class="discover-results-area">

            <!-- Error banner -->
            <div v-if="errorMessage" class="alert alert-danger mb-3" role="alert">{{ errorMessage }}</div>

            <!-- Loading skeleton -->
            <div v-if="submitting && !summaryMarkdown" class="card discover-card shadow-sm border-0">
                <div class="card-body discover-results-placeholder">
                    <div class="spinner-border text-primary mb-3" role="status">
                        <span class="visually-hidden">Loading…</span>
                    </div>
                    <p class="text-muted mb-0">Reading the catalog…</p>
                </div>
                <div :class="[$store.state.darkMode ? 'loading-content-card-dark' : 'loading-content-card']">
                    <div class="content"><h2></h2><p></p></div>
                </div>
            </div>

            <!-- Results tabs -->
            <div v-else-if="hasResults">

                <!-- KPI summary strip — surface the headline before the detail -->
                <div v-if="mode === 'catalog'" class="dmd-kpi-row mb-3">
                    <div class="dmd-kpi">
                        <span class="dmd-kpi-value">{{ tables.length }}</span>
                        <span class="dmd-kpi-label">Tables Scanned</span>
                    </div>
                    <div class="dmd-kpi" :class="ingestionReadyCount ? 'dmd-kpi--ok' : 'dmd-kpi--muted'">
                        <span class="dmd-kpi-value">{{ ingestionReadyCount }}</span>
                        <span class="dmd-kpi-label">Ingestion Ready</span>
                    </div>
                    <div class="dmd-kpi" :class="lookupReadyCount ? 'dmd-kpi--ok' : 'dmd-kpi--muted'">
                        <span class="dmd-kpi-value">{{ lookupReadyCount }}</span>
                        <span class="dmd-kpi-label">Lookup Ready</span>
                    </div>
                    <div class="dmd-kpi" :class="{ 'dmd-kpi--warn': databasesScanned < databasesTotal }">
                        <span class="dmd-kpi-value">
                            {{ databasesScanned }}<span v-if="databasesTotal" class="dmd-kpi-value-sub">/{{ databasesTotal }}</span>
                        </span>
                        <span class="dmd-kpi-label">Databases Scanned</span>
                    </div>
                </div>
                <div v-else class="dmd-kpi-row mb-3">
                    <div class="dmd-kpi">
                        <span class="dmd-kpi-value">{{ datasets.length }}</span>
                        <span class="dmd-kpi-label">Files Scanned</span>
                    </div>
                    <div class="dmd-kpi" :class="datasetIngestionReadyCount ? 'dmd-kpi--ok' : 'dmd-kpi--muted'">
                        <span class="dmd-kpi-value">{{ datasetIngestionReadyCount }}</span>
                        <span class="dmd-kpi-label">Ingestion Ready</span>
                    </div>
                    <div class="dmd-kpi" :class="datasetLookupReadyCount ? 'dmd-kpi--ok' : 'dmd-kpi--muted'">
                        <span class="dmd-kpi-value">{{ datasetLookupReadyCount }}</span>
                        <span class="dmd-kpi-label">Lookup Ready</span>
                    </div>
                    <div class="dmd-kpi" :class="datasetRegisteredCount ? 'dmd-kpi--ok' : 'dmd-kpi--muted'">
                        <span class="dmd-kpi-value">{{ datasetRegisteredCount }}</span>
                        <span class="dmd-kpi-label">Registered in Catalog</span>
                    </div>
                </div>

                <Tabs :tabs="tabItems" :btnTabs="true">

                    <!-- Summary -->
                    <template v-slot:summary>
                        <div class="card">
                            <div class="card-body markdown-content discover-markdown">
                                <Markdown :content="summaryMarkdown" />
                            </div>
                        </div>
                    </template>

                    <!-- Datasets (Scan S3 Files mode) -->
                    <template v-slot:datasets>
                        <div v-if="!datasets.length" class="card">
                            <div class="card-body text-center text-muted py-5">No files found for this scope.</div>
                        </div>
                        <div v-for="(ds, idx) in datasets" :key="idx" class="card mb-3"
                            :class="(ds.zcr_compatible || ds.zcr_lookup?.compatible)
                                ? 'border-start border-4 border-success'
                                : 'border-start border-4 border-secondary-subtle'">
                            <div class="card-body">
                                <div class="d-flex align-items-start justify-content-between gap-2 flex-wrap mb-2">
                                    <div>
                                        <h5 class="mb-1 fw-semibold">{{ ds.name }}</h5>
                                        <p v-if="ds.data_type_reason" class="text-muted small mb-0">{{ ds.data_type_reason }}</p>
                                    </div>
                                    <div class="d-flex gap-2 flex-wrap flex-shrink-0">
                                        <span class="ds-badge ds-badge--format">{{ (ds.format || '').toUpperCase() }}</span>
                                        <span v-if="ds.data_type" class="ds-badge ds-badge--type">{{ formatDataType(ds.data_type) }}</span>
                                        <span v-if="ds.catalog_match" class="ds-badge ds-badge--zcr-ok">
                                            <i class="fa fa-link me-1" aria-hidden="true"></i>
                                            {{ ds.catalog_match.database }}.{{ ds.catalog_match.table }}
                                        </span>
                                        <span v-else-if="enrichWithCatalog" class="ds-badge ds-badge--format">
                                            <i class="fa fa-circle-exclamation me-1" aria-hidden="true"></i>Not in catalog
                                        </span>
                                    </div>
                                </div>
                                <div class="mb-3">
                                    <div class="ds-label">Location</div>
                                    <code class="ds-location">{{ ds.location }}</code>
                                </div>
                                <div v-if="ds.columns?.length" class="mb-3">
                                    <div class="ds-label">Columns ({{ ds.columns.length }})</div>
                                    <div class="d-flex flex-wrap gap-1 mt-1">
                                        <span v-for="col in ds.columns" :key="col" class="ds-col-badge">{{ col }}</span>
                                    </div>
                                </div>

                                <div class="row g-2">
                                    <div class="col-12 col-md-6">
                                        <div class="zcr-check-card">
                                            <div class="d-flex align-items-center justify-content-between mb-1">
                                                <span class="ds-label mb-0"><i class="fa fa-diagram-project me-1" aria-hidden="true"></i>ZCR Ingestion</span>
                                                <span v-if="ds.zcr_compatible" class="ds-badge ds-badge--zcr-ok">
                                                    <i class="fa fa-circle-check me-1"></i>Ready
                                                </span>
                                                <span v-else class="ds-badge ds-badge--zcr-warn">Not Ready</span>
                                            </div>
                                            <ul v-if="!ds.zcr_compatible" class="mb-0 ps-3 small text-muted">
                                                <li v-for="issue in ds.zcr_issues || []" :key="issue">{{ issue }}</li>
                                            </ul>
                                        </div>
                                    </div>
                                    <div class="col-12 col-md-6">
                                        <div class="zcr-check-card">
                                            <div class="d-flex align-items-center justify-content-between mb-1">
                                                <span class="ds-label mb-0"><i class="fa fa-bolt me-1" aria-hidden="true"></i>ZCR Lookup Index</span>
                                                <span v-if="!enrichWithCatalog" class="ds-badge ds-badge--format">Not checked</span>
                                                <span v-else-if="!ds.catalog_match" class="ds-badge ds-badge--format">Unknown — not in catalog</span>
                                                <span v-else-if="ds.zcr_lookup?.compatible" class="ds-badge ds-badge--zcr-ok">
                                                    <i class="fa fa-circle-check me-1"></i>Ready
                                                </span>
                                                <span v-else class="ds-badge ds-badge--zcr-warn">Not Ready</span>
                                            </div>
                                            <div v-if="ds.catalog_match?.iceberg_managed" class="small text-muted">
                                                <i class="fa fa-triangle-exclamation me-1" aria-hidden="true"></i>
                                                Iceberg-managed — direct-Parquet lookup doesn't apply here.
                                            </div>
                                            <ul v-else-if="ds.catalog_match && !ds.zcr_lookup?.compatible" class="mb-0 ps-3 small text-muted">
                                                <li v-for="issue in ds.zcr_lookup?.issues || []" :key="issue">{{ issue }}</li>
                                            </ul>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </template>

                    <!-- Tables -->
                    <template v-slot:tables>
                        <div v-if="!tables.length" class="card">
                            <div class="card-body text-center text-muted py-5">No tables found in the catalog.</div>
                        </div>
                        <template v-else>
                            <div class="dmd-table-toolbar mb-3">
                                <div class="dmd-filter-chips">
                                    <button v-for="f in tableStatusFilters" :key="f.key" type="button"
                                        class="dmd-chip" :class="{ active: tableStatusFilter === f.key }"
                                        @click="tableStatusFilter = f.key">
                                        {{ f.label }} <span class="dmd-chip-count">{{ f.count }}</span>
                                    </button>
                                </div>
                                <div class="dmd-table-search">
                                    <i class="fa fa-magnifying-glass" aria-hidden="true"></i>
                                    <input v-model.trim="tableSearchQuery" type="search"
                                        class="form-control form-control-sm" placeholder="Filter by name…" />
                                </div>
                            </div>

                            <div v-if="!visibleTables.length" class="card">
                                <div class="card-body text-center text-muted py-5">
                                    No tables match this filter.
                                </div>
                            </div>

                            <div v-for="{ table, idx } in visibleTables" :key="idx" class="card mb-3 dmd-table-card"
                                :class="(table.zcr_ingestion?.compatible || table.zcr_lookup?.compatible)
                                    ? 'border-start border-4 border-success'
                                    : 'border-start border-4 border-secondary-subtle'">
                            <div class="card-body">
                                <div class="d-flex align-items-start justify-content-between gap-2 flex-wrap mb-2">
                                    <div>
                                        <h5 class="mb-1 fw-semibold">{{ table.database }}.{{ table.table }}</h5>
                                        <p v-if="table.data_type_reason" class="text-muted small mb-0">
                                            {{ table.data_type_reason }}
                                        </p>
                                    </div>
                                    <div class="d-flex gap-2 flex-wrap flex-shrink-0">
                                        <span class="ds-badge ds-badge--format">{{ (table.classification || 'unknown').toUpperCase() }}</span>
                                        <span v-if="table.data_type" class="ds-badge ds-badge--type">
                                            {{ formatDataType(table.data_type) }}
                                        </span>
                                    </div>
                                </div>
                                <div class="mb-3">
                                    <div class="ds-label">Location (from catalog)</div>
                                    <code class="ds-location">{{ table.location || '—' }}</code>
                                </div>

                                <div class="row g-2">
                                    <div class="col-12 col-md-6">
                                        <div class="zcr-check-card">
                                            <div class="d-flex align-items-center justify-content-between mb-1">
                                                <span class="ds-label mb-0"><i class="fa fa-diagram-project me-1" aria-hidden="true"></i>ZCR Ingestion</span>
                                                <span v-if="table.zcr_ingestion?.compatible" class="ds-badge ds-badge--zcr-ok">
                                                    <i class="fa fa-circle-check me-1"></i>Ready
                                                </span>
                                                <span v-else class="ds-badge ds-badge--zcr-warn">Not Ready</span>
                                            </div>
                                            <div v-if="table.zcr_ingestion?.compatible" class="small text-muted">
                                                identity: <code>{{ table.zcr_ingestion.identity_col }}</code>
                                                <span v-if="table.zcr_ingestion.timestamp_col">
                                                    · timestamp: <code>{{ table.zcr_ingestion.timestamp_col }}</code>
                                                </span>
                                                <span v-if="table.zcr_ingestion.schema"> · schema: <code>{{ table.zcr_ingestion.schema }}</code></span>
                                            </div>
                                            <ul v-else class="mb-0 ps-3 small text-muted">
                                                <li v-for="issue in table.zcr_ingestion?.issues || []" :key="issue">{{ issue }}</li>
                                            </ul>
                                        </div>
                                    </div>
                                    <div class="col-12 col-md-6">
                                        <div class="zcr-check-card">
                                            <div class="d-flex align-items-center justify-content-between mb-1">
                                                <span class="ds-label mb-0"><i class="fa fa-bolt me-1" aria-hidden="true"></i>ZCR Lookup Index</span>
                                                <span v-if="table.zcr_lookup?.compatible" class="ds-badge ds-badge--zcr-ok">
                                                    <i class="fa fa-circle-check me-1"></i>Ready
                                                </span>
                                                <span v-else class="ds-badge ds-badge--zcr-warn">Not Ready</span>
                                            </div>
                                            <div v-if="table.zcr_lookup?.compatible" class="small text-muted">
                                                primary key: <code>{{ table.zcr_lookup.primary_id_candidate }}</code>
                                            </div>
                                            <ul v-else class="mb-0 ps-3 small text-muted">
                                                <li v-for="issue in table.zcr_lookup?.issues || []" :key="issue">{{ issue }}</li>
                                            </ul>
                                        </div>
                                    </div>
                                </div>

                                <!-- Inspect: identify via catalog, then scan its exact S3 folder for detail -->
                                <div class="mt-3 pt-3 dmd-inspect-divider">
                                    <button class="btn btn-sm btn-outline-primary" type="button"
                                        :disabled="inspecting[idx] || !credentialFilter"
                                        :title="!credentialFilter ? 'Pick a Credential above first' : ''"
                                        @click="inspectTable(table, idx)">
                                        <span v-if="inspecting[idx]" class="spinner-border spinner-border-sm me-1" role="status" />
                                        <i v-else class="fa fa-magnifying-glass-chart me-1"></i>
                                        {{ inspecting[idx] ? 'Scanning folder…' : 'Inspect (scan its S3 folder)' }}
                                    </button>

                                    <div v-if="inspectErrors[idx]" class="alert alert-danger small py-2 px-3 mt-2 mb-0">
                                        {{ inspectErrors[idx] }}
                                    </div>

                                    <div v-if="inspectResults[idx]" class="dmd-inspect-result mt-3">
                                        <div class="ds-label">Scanned folder</div>
                                        <code class="ds-location d-block mb-2">{{ inspectResults[idx].bucket_scope }}</code>

                                        <div v-if="!inspectResults[idx].files.length" class="text-muted small">
                                            {{ inspectResults[idx].summary_markdown }}
                                        </div>
                                        <template v-else>
                                            <div class="d-flex align-items-center gap-2 flex-wrap mb-2">
                                                <span class="ds-badge ds-badge--type">
                                                    Rescanned as: {{ formatDataType(inspectResults[idx].data_type) }}
                                                </span>
                                                <span class="text-muted small">{{ inspectResults[idx].data_type_reason }}</span>
                                            </div>

                                            <div class="ds-label">Files sampled ({{ inspectResults[idx].files.length }})</div>
                                            <div class="dmd-file-list mb-3">
                                                <div v-for="f in inspectResults[idx].files" :key="f.path" class="dmd-file-row">
                                                    <code class="small">{{ f.path.split('/').pop() }}</code>
                                                    <span class="text-muted small">{{ f.format }} · {{ f.row_count }} row(s) sampled</span>
                                                </div>
                                            </div>

                                            <div class="row g-2">
                                                <div class="col-12 col-md-6">
                                                    <div class="zcr-check-card">
                                                        <div class="d-flex align-items-center justify-content-between mb-1">
                                                            <span class="ds-label mb-0">ZCR Ingestion (rescanned)</span>
                                                            <span v-if="inspectResults[idx].zcr_ingestion.compatible" class="ds-badge ds-badge--zcr-ok">Ready</span>
                                                            <span v-else class="ds-badge ds-badge--zcr-warn">Not Ready</span>
                                                        </div>
                                                        <ul v-if="!inspectResults[idx].zcr_ingestion.compatible" class="mb-0 ps-3 small text-muted">
                                                            <li v-for="issue in inspectResults[idx].zcr_ingestion.issues" :key="issue">{{ issue }}</li>
                                                        </ul>
                                                    </div>
                                                </div>
                                                <div class="col-12 col-md-6">
                                                    <div class="zcr-check-card">
                                                        <div class="d-flex align-items-center justify-content-between mb-1">
                                                            <span class="ds-label mb-0">ZCR Lookup Index (rescanned)</span>
                                                            <span v-if="inspectResults[idx].zcr_lookup.compatible" class="ds-badge ds-badge--zcr-ok">Ready</span>
                                                            <span v-else class="ds-badge ds-badge--zcr-warn">Not Ready</span>
                                                        </div>
                                                        <ul v-if="!inspectResults[idx].zcr_lookup.compatible" class="mb-0 ps-3 small text-muted">
                                                            <li v-for="issue in inspectResults[idx].zcr_lookup.issues" :key="issue">{{ issue }}</li>
                                                        </ul>
                                                    </div>
                                                </div>
                                            </div>
                                            <p class="text-muted small mt-2 mb-0">
                                                This replaces the catalog's shallow classification for this table in the
                                                <strong>Deploy Pipeline</strong> tab — job stacks there now use this scan's result.
                                            </p>
                                        </template>
                                    </div>
                                </div>
                            </div>
                        </div>
                        </template>
                    </template>

                    <!-- Deploy Pipeline -->
                    <template v-slot:deploy>
                        <div v-if="!deployableItems.length" class="card">
                            <div class="card-body text-center text-muted py-5">
                                No ZCR-ready job stacks available for deployment yet.
                            </div>
                        </div>
                        <div v-for="(item, idx) in deployableItems" :key="idx" class="card mb-3">
                            <div class="card-header border-0 d-flex align-items-center justify-content-between">
                                <div class="d-flex align-items-center gap-2">
                                    <i class="fa fa-rocket text-primary"></i>
                                    <span class="fw-semibold">{{ item.tableLabel }}</span>
                                    <span class="ds-badge ds-badge--type">{{ item.kind }}</span>
                                </div>
                                <span class="ds-badge ds-badge--zcr-ok">
                                    <i class="fa fa-circle-check me-1"></i>ZCR Ready
                                </span>
                            </div>
                            <div class="card-body">
                                <div class="deploy-pipeline-name mb-3">
                                    <i class="fa fa-layer-group me-2 text-muted"></i>
                                    Pipeline: <code>{{ item.config?.stack_name }}</code>
                                </div>
                                <div class="mb-3">
                                    <button class="btn btn-sm btn-outline-secondary" type="button"
                                        @click="toggleDeployPreview(idx)">
                                        <i class="fa fa-eye me-1"></i>
                                        {{ deployStates[idx]?.showPreview ? 'Hide' : 'Show' }} Job Stack Config
                                    </button>
                                    <div v-if="deployStates[idx]?.showPreview" class="mt-2">
                                        <div class="deploy-config-tabs mb-2">
                                            <button v-for="tab in ['Create Request', 'Deploy Request']" :key="tab"
                                                class="deploy-config-tab"
                                                :class="{ active: deployStates[idx].previewTab === tab }"
                                                @click="deployStates[idx].previewTab = tab">{{ tab }}</button>
                                        </div>
                                        <pre class="discover-code-block">{{ deployStates[idx].previewTab === 'Create Request'
                                            ? JSON.stringify(item.config?.create_request, null, 2)
                                            : JSON.stringify(item.config?.deploy_request, null, 2) }}</pre>
                                    </div>
                                </div>
                                <div v-if="deployStates[idx]?.deployed"
                                    class="alert alert-success d-flex align-items-center gap-2 mb-0">
                                    <i class="fa fa-circle-check fa-lg"></i>
                                    <span>Pipeline <strong>{{ item.config?.stack_name }}</strong>
                                        deployed to <strong>{{ deployStates[idx].sandboxName }}</strong>.</span>
                                </div>
                                <template v-else>
                                    <div class="row g-2 align-items-end">
                                        <div class="col">
                                            <label class="form-label small fw-semibold mb-1">Target Sandbox</label>
                                            <select class="form-select form-select-sm"
                                                v-model="deployStates[idx].sandboxName"
                                                :disabled="deployStates[idx].creating || deployStates[idx].deploying">
                                                <option value="">Select sandbox…</option>
                                                <option v-for="sb in sandboxOptions" :key="sb.value" :value="sb.value">
                                                    {{ sb.label }}
                                                </option>
                                            </select>
                                        </div>
                                        <div class="col-auto">
                                            <button type="button" class="btn btn-primary btn-sm"
                                                :disabled="!deployStates[idx]?.sandboxName || deployStates[idx]?.creating || deployStates[idx]?.deploying"
                                                @click="createAndDeploy(item, idx)">
                                                <span v-if="deployStates[idx]?.creating || deployStates[idx]?.deploying"
                                                    class="spinner-border spinner-border-sm me-1" role="status" />
                                                <i v-else class="fa fa-rocket me-1"></i>
                                                <span v-if="deployStates[idx]?.creating">Creating…</span>
                                                <span v-else-if="deployStates[idx]?.deploying">Deploying…</span>
                                                <span v-else>Create &amp; Deploy</span>
                                            </button>
                                        </div>
                                    </div>
                                    <div v-if="deployStates[idx]?.error"
                                        class="alert alert-danger small py-2 px-3 mt-2 mb-0">
                                        {{ deployStates[idx].error }}
                                    </div>
                                    <div v-if="deployStates[idx]?.creating || deployStates[idx]?.deploying"
                                        class="deploy-steps mt-3">
                                        <div class="deploy-step"
                                            :class="{ active: deployStates[idx]?.creating, done: !deployStates[idx]?.creating && (deployStates[idx]?.deploying || deployStates[idx]?.deployed) }">
                                            <span class="deploy-step__dot"></span>
                                            <span class="deploy-step__label">Create job stack</span>
                                        </div>
                                        <div class="deploy-step"
                                            :class="{ active: deployStates[idx]?.deploying, done: deployStates[idx]?.deployed }">
                                            <span class="deploy-step__dot"></span>
                                            <span class="deploy-step__label">Deploy to sandbox</span>
                                        </div>
                                    </div>
                                </template>
                            </div>
                        </div>
                    </template>

                </Tabs>
            </div>

            <!-- Empty state / hero -->
            <div v-else class="dmd-hero">
                <div class="dmd-hero-icon">
                    <i class="fa fa-wand-magic-sparkles" aria-hidden="true"></i>
                </div>
                <h4 class="dmd-hero-title">Explore your data catalog</h4>
                <p class="dmd-hero-subtitle">
                    Optionally narrow by database above, then scan — or jump straight to what you need.
                </p>
                <div class="dmd-hero-suggestions">
                    <button type="button" class="dmd-suggestion" :disabled="submitDisabled" @click="runSuggestion()">
                        <i class="fa fa-layer-group" aria-hidden="true"></i> Scan the whole catalog
                    </button>
                    <button type="button" class="dmd-suggestion" :disabled="submitDisabled" @click="runSuggestion('lookup')">
                        <i class="fa fa-bolt" aria-hidden="true"></i> Find ZCR lookup–ready tables
                    </button>
                    <button type="button" class="dmd-suggestion" :disabled="submitDisabled" @click="runSuggestion('ingestion')">
                        <i class="fa fa-diagram-project" aria-hidden="true"></i> Find ZCR ingestion–ready tables
                    </button>
                </div>
            </div>

        </div><!-- end results -->
    </div>
</template>

<script>
import axios from "axios";
import { mapState, mapActions } from "vuex";
import NavHeader from "../../components/NavHeader.vue";
import Markdown from "../../components/Markdown.vue";
import Tabs from "../../components/Tabs.vue";

const MAX_DATABASES_HINT = 20; // mirrors MAX_CATALOG_DATABASES_TO_SCAN in the agents backend

export default {
    name: "DiscoverMyData",
    components: { NavHeader, Markdown, Tabs },
    data() {
        return {
            MAX_DATABASES_HINT,

            // Mode: "catalog" (bulk-scan the Glue Catalog) or "scan" (file-browser-first,
            // catalog-enriching — also finds data the catalog doesn't know about yet)
            mode: "catalog",

            // Scope state
            credentialFilter: "",
            credentialList: [],
            credentialLoading: false,
            databaseFilter: "",
            databaseList: [],
            databasesLoading: false,
            tableFilter: "",
            tableList: [],
            tablesLoading: false,
            catalogError: "",

            // Scan-mode scope state
            bucketFilter: "",
            bucketList: [],
            bucketsLoading: false,
            prefixFilter: "",
            scanPrompt: "",
            enrichWithCatalog: true,

            // Subfolder picker (browses the selected bucket via the Surface file browser,
            // one level at a time, instead of requiring a hand-typed prefix)
            subfolderMenuOpen: false,
            subfolderOptions: [],
            subfoldersLoading: false,
            subfolderError: "",

            // Query state
            submitting: false,
            errorMessage: "",

            // Results state
            summaryMarkdown: "",
            tables: [],
            datasets: [],
            databasesScanned: 0,
            databasesTotal: 0,
            deployStates: {},

            // Inspect state (catalog identify -> credential-scoped file scan), keyed by
            // the table's index in `tables`
            inspecting: {},
            inspectResults: {},
            inspectErrors: {},

            // Tables tab filter/search
            tableStatusFilter: "all",
            tableSearchQuery: "",
        };
    },
    computed: {
        ...mapState({
            currentClient: (state) => state.currentClient,
            accessToken: (state) => state.accessToken,
        }),
        agentsBase() {
            return this.$store.state.agentsUrl || this.$store.state.defaultUrls?.agents || "";
        },
        submitDisabled() {
            return (
                this.submitting ||
                !this.currentClient?.cid ||
                !this.agentsBase ||
                (!!this.tableFilter && !this.databaseFilter)
            );
        },
        scanDisabled() {
            return (
                this.submitting ||
                !this.currentClient?.cid ||
                !this.agentsBase ||
                !this.credentialFilter ||
                !this.bucketFilter ||
                !this.scanPrompt.trim()
            );
        },
        hasResults() {
            return !!(this.summaryMarkdown || this.tables.length || this.datasets.length);
        },
        deployableItems() {
            const items = [];
            if (this.mode === "scan") {
                this.datasets.forEach((ds) => {
                    if (ds.zcr_compatible && ds.job_stack_config) {
                        items.push({ kind: "Ingestion", tableLabel: ds.name, config: ds.job_stack_config });
                    }
                    if (ds.zcr_lookup?.compatible && ds.zcr_lookup_job_stack) {
                        items.push({ kind: "Lookup", tableLabel: ds.name, config: ds.zcr_lookup_job_stack });
                    }
                });
                return items;
            }
            this.tables.forEach((table, idx) => {
                const label = `${table.database}.${table.table}`;
                // An Inspect scan is based on real sample data, not just the catalog's
                // schema-only classification — prefer it over the bulk-scan result once run.
                const inspected = this.inspectResults[idx];
                const ingestion = inspected?.zcr_ingestion ?? table.zcr_ingestion;
                const ingestionStack = inspected?.zcr_ingestion_job_stack ?? table.zcr_ingestion_job_stack;
                const lookup = inspected?.zcr_lookup ?? table.zcr_lookup;
                const lookupStack = inspected?.zcr_lookup_job_stack ?? table.zcr_lookup_job_stack;
                const source = inspected ? " (inspected)" : "";
                if (ingestion?.compatible && ingestionStack) {
                    items.push({ kind: `Ingestion${source}`, tableLabel: label, config: ingestionStack });
                }
                if (lookup?.compatible && lookupStack) {
                    items.push({ kind: `Lookup${source}`, tableLabel: label, config: lookupStack });
                }
            });
            return items;
        },
        ingestionReadyCount() {
            return this.tables.filter((t) => t.zcr_ingestion?.compatible).length;
        },
        lookupReadyCount() {
            return this.tables.filter((t) => t.zcr_lookup?.compatible).length;
        },
        notReadyCount() {
            return this.tables.filter((t) => !t.zcr_ingestion?.compatible && !t.zcr_lookup?.compatible).length;
        },
        datasetIngestionReadyCount() {
            return this.datasets.filter((d) => d.zcr_compatible).length;
        },
        datasetLookupReadyCount() {
            return this.datasets.filter((d) => d.zcr_lookup?.compatible).length;
        },
        datasetRegisteredCount() {
            return this.datasets.filter((d) => d.catalog_match).length;
        },
        tableStatusFilters() {
            return [
                { key: "all", label: "All", count: this.tables.length },
                { key: "ingestion", label: "Ingestion Ready", count: this.ingestionReadyCount },
                { key: "lookup", label: "Lookup Ready", count: this.lookupReadyCount },
                { key: "not-ready", label: "Not Ready", count: this.notReadyCount },
            ];
        },
        visibleTables() {
            // Carry the original index (`_idx`) through filtering/search so per-table
            // state (inspectResults, deployStates, etc.) keyed by that index still lines up.
            let list = this.tables.map((table, idx) => ({ table, idx }));
            if (this.tableStatusFilter === "ingestion") {
                list = list.filter((r) => r.table.zcr_ingestion?.compatible);
            } else if (this.tableStatusFilter === "lookup") {
                list = list.filter((r) => r.table.zcr_lookup?.compatible);
            } else if (this.tableStatusFilter === "not-ready") {
                list = list.filter((r) => !r.table.zcr_ingestion?.compatible && !r.table.zcr_lookup?.compatible);
            }
            if (this.tableSearchQuery) {
                const q = this.tableSearchQuery.toLowerCase();
                list = list.filter((r) => `${r.table.database}.${r.table.table}`.toLowerCase().includes(q));
            }
            return list;
        },
        sandboxOptions() {
            return (this.$store.state.sandbox?.sandboxes || []).map(sb => ({
                label: `${sb.name} (${sb.env})`,
                value: sb.name,
            }));
        },
        tabItems() {
            // The structured, scannable view leads over the markdown wall for a result
            // set that can run into the hundreds of rows.
            if (this.mode === "scan") {
                return [
                    { name: "Datasets", id: "dmd-datasets", slot: "datasets" },
                    { name: "Summary", id: "dmd-summary", slot: "summary" },
                    { name: "Deploy Pipeline", id: "dmd-deploy", slot: "deploy" },
                ];
            }
            return [
                { name: "Tables", id: "dmd-tables", slot: "tables" },
                { name: "Summary", id: "dmd-summary", slot: "summary" },
                { name: "Deploy Pipeline", id: "dmd-deploy", slot: "deploy" },
            ];
        },
    },
    watch: {
        mode() {
            // Switching modes invalidates the other mode's results — avoid showing stale
            // tables/datasets, or index-keyed state (deployStates, inspect*) lining up wrong.
            this.summaryMarkdown = "";
            this.tables = [];
            this.datasets = [];
            this.errorMessage = "";
            this.catalogError = "";
            this.inspecting = {};
            this.inspectResults = {};
            this.inspectErrors = {};
        },
        deployableItems(items) {
            const states = {};
            items.forEach((_, idx) => {
                states[idx] = {
                    showPreview: false,
                    previewTab: "Create Request",
                    sandboxName: "",
                    creating: false,
                    deploying: false,
                    deployed: false,
                    error: "",
                };
            });
            this.deployStates = states;
        },
        databaseFilter(newVal) {
            this.tableFilter = "";
            this.tableList = [];
            if (newVal) this.fetchTables(newVal);
        },
        credentialFilter(newVal) {
            this.bucketFilter = "";
            this.bucketList = [];
            if (newVal) this.fetchBuckets(newVal);
        },
        bucketFilter() {
            // A new bucket invalidates the previous prefix and any subfolder listing for it.
            this.prefixFilter = "";
            this.subfolderOptions = [];
            this.subfolderError = "";
            this.subfolderMenuOpen = false;
        },
    },
    mounted() {
        this.fetchSandboxes();
        this.fetchCredentials();
        this.fetchDatabases();
        document.addEventListener("mousedown", this.handleSubfolderOutsideClick);
    },
    beforeUnmount() {
        document.removeEventListener("mousedown", this.handleSubfolderOutsideClick);
    },
    methods: {
        ...mapActions({
            fetchSandboxes: "sandbox/fetchSandboxes",
            upsertJobStack: "amdp/upsertJobStack",
            deployJobStack: "amdp/deployJobStack",
            getServiceNamespaces: "amdp/getServiceNamespaces",
            getServiceBucketList: "amdp/getServiceBucketList",
            getServiceFileBrowserList: "amdp/getServiceFileBrowserList",
        }),

        // ── Subfolder picker ──────────────────────────────────────────────
        handleSubfolderOutsideClick(evt) {
            if (this.subfolderMenuOpen && this.$refs.subfolderPill && !this.$refs.subfolderPill.contains(evt.target)) {
                this.subfolderMenuOpen = false;
            }
        },
        closeSubfolderMenu() {
            this.subfolderMenuOpen = false;
        },
        toggleSubfolderMenu() {
            if (this.subfolderMenuOpen) {
                this.subfolderMenuOpen = false;
                return;
            }
            this.subfolderMenuOpen = true;
            this.fetchSubfolders();
        },
        currentFolderScopePath() {
            const prefix = (this.prefixFilter || "").replace(/^\/+/, "");
            return `s3://${this.currentClient.cid}.${this.credentialFilter}@${this.bucketFilter}/${prefix}`
                .replace(/([^:]\/)\/+/g, "$1");
        },
        async fetchSubfolders() {
            if (!this.credentialFilter || !this.bucketFilter) return;
            this.subfoldersLoading = true;
            this.subfolderError = "";
            try {
                const res = await this.getServiceFileBrowserList({
                    serviceName: "surface",
                    namespace: this.credentialFilter,
                    folderName: this.currentFolderScopePath(),
                });
                const items = Array.isArray(res.data) ? res.data : [];
                this.subfolderOptions = items
                    .filter((it) => String(it.is_dir).toLowerCase() === "true")
                    .map((it) => it.name)
                    .filter(Boolean)
                    .sort();
            } catch (e) {
                this.subfolderOptions = [];
                this.subfolderError = e.response?.data?.error || e.message || "Could not list subfolders.";
            } finally {
                this.subfoldersLoading = false;
            }
        },
        selectSubfolder(name) {
            const clean = name.endsWith("/") ? name : `${name}/`;
            const base = this.prefixFilter ? `${this.prefixFilter.replace(/\/+$/, "")}/` : "";
            this.prefixFilter = `${base}${clean}`;
            this.fetchSubfolders();
        },
        goUpSubfolder() {
            const segments = this.prefixFilter.replace(/\/+$/, "").split("/").filter(Boolean);
            segments.pop();
            this.prefixFilter = segments.length ? `${segments.join("/")}/` : "";
            this.fetchSubfolders();
        },

        async fetchCredentials() {
            this.credentialLoading = true;
            try {
                const res = await this.getServiceNamespaces("surface");
                this.credentialList = res.data || [];
                if (this.credentialList.length === 1) {
                    this.credentialFilter = this.credentialList[0];
                }
            } catch (e) {
                this.catalogError = e.response?.data?.error || e.message;
            } finally {
                this.credentialLoading = false;
            }
        },
        async fetchBuckets(credential) {
            if (!this.currentClient?.cid) return;
            this.bucketsLoading = true;
            try {
                const res = await this.getServiceBucketList({
                    serviceName: "surface",
                    namespace: credential,
                    type: "s3",
                    credential: `${this.currentClient.cid}.${credential}`,
                });
                this.bucketList = (res.data || []).map((b) => b.name);
            } catch (e) {
                this.catalogError = e.response?.data?.error || e.message;
            } finally {
                this.bucketsLoading = false;
            }
        },
        async fetchDatabases() {
            if (!this.currentClient?.cid || !this.agentsBase) return;
            this.databasesLoading = true;
            try {
                const url = `${this.agentsBase.replace(/\/$/, "")}/v1/discover-my-data/catalog/databases`;
                const { data } = await axios.post(
                    url,
                    { cid: this.currentClient.cid },
                    { headers: { Authorization: `Bearer ${this.accessToken}`, "Content-Type": "application/json" } }
                );
                this.databaseList = data?.databases || [];
            } catch (e) {
                this.catalogError = e.response?.data?.error || e.message;
            } finally {
                this.databasesLoading = false;
            }
        },
        async fetchTables(database) {
            this.tablesLoading = true;
            try {
                const url = `${this.agentsBase.replace(/\/$/, "")}/v1/discover-my-data/catalog/tables`;
                const { data } = await axios.post(
                    url,
                    { cid: this.currentClient.cid, database },
                    { headers: { Authorization: `Bearer ${this.accessToken}`, "Content-Type": "application/json" } }
                );
                this.tableList = data?.tables || [];
            } catch (e) {
                this.catalogError = e.response?.data?.error || e.message;
            } finally {
                this.tablesLoading = false;
            }
        },
        async inspectTable(table, idx) {
            if (!this.credentialFilter) return;
            this.inspectErrors = { ...this.inspectErrors, [idx]: "" };
            this.inspecting = { ...this.inspecting, [idx]: true };
            try {
                const url = `${this.agentsBase.replace(/\/$/, "")}/v1/discover-my-data/catalog/inspect`;
                const { data } = await axios.post(
                    url,
                    {
                        cid: this.currentClient.cid,
                        credential: this.credentialFilter,
                        database: table.database,
                        table: table.table,
                    },
                    {
                        headers: {
                            Authorization: `Bearer ${this.accessToken}`,
                            "Content-Type": "application/json",
                        },
                    }
                );
                this.inspectResults = { ...this.inspectResults, [idx]: data };
            } catch (err) {
                const msg = err.response?.data?.error || err.response?.data?.message || err.message || "Inspect failed";
                this.inspectErrors = { ...this.inspectErrors, [idx]: typeof msg === "string" ? msg : JSON.stringify(msg) };
            } finally {
                this.inspecting = { ...this.inspecting, [idx]: false };
            }
        },

        formatDataType(type) {
            if (!type) return "";
            return type.replace(/_/g, " ").replace(/\b\w/g, c => c.toUpperCase());
        },
        toggleDeployPreview(idx) {
            if (this.deployStates[idx]) {
                this.deployStates[idx].showPreview = !this.deployStates[idx].showPreview;
            }
        },
        async createAndDeploy(item, idx) {
            const state = this.deployStates[idx];
            const config = item.config;
            state.error = "";
            state.creating = true;
            try {
                await this.upsertJobStack(config.create_request);
            } catch (err) {
                state.error = err.response?.data?.error || err.message || "Failed to create job stack.";
                state.creating = false;
                return;
            }
            state.creating = false;
            state.deploying = true;
            try {
                const deployPayload = {
                    ...config.deploy_request,
                    cid: this.currentClient.cid,
                    sandbox: state.sandboxName,
                };
                await this.deployJobStack({ name: config.stack_name, data: deployPayload });
                state.deployed = true;
                this.$store.dispatch("addToast", {
                    message: `Pipeline "${config.stack_name}" deployed to ${state.sandboxName}.`,
                    type: "success",
                });
            } catch (err) {
                state.error = err.response?.data?.error || err.message || "Failed to deploy job stack.";
            } finally {
                state.deploying = false;
            }
        },
        async runSuggestion(statusFilter) {
            this.databaseFilter = "";
            this.tableFilter = "";
            await this.scanCatalog();
            if (statusFilter) this.tableStatusFilter = statusFilter;
        },
        async scanCatalog() {
            this.errorMessage = "";
            this.catalogError = "";
            this.summaryMarkdown = "";
            this.tables = [];
            // Inspect results are keyed by index into `tables` — a fresh scan invalidates them.
            this.inspecting = {};
            this.inspectResults = {};
            this.inspectErrors = {};
            this.tableStatusFilter = "all";
            this.tableSearchQuery = "";
            if (this.submitDisabled && !this.submitting) return;
            this.submitting = true;
            const payload = { cid: this.currentClient.cid };
            if (this.databaseFilter) {
                payload.database = this.databaseFilter;
                if (this.tableFilter) payload.table = this.tableFilter;
            }
            try {
                const url = `${this.agentsBase.replace(/\/$/, "")}/v1/discover-my-data/catalog/zcr-lookup-readiness`;
                const { data } = await axios.post(url, payload, {
                    headers: {
                        Authorization: `Bearer ${this.accessToken}`,
                        "Content-Type": "application/json",
                    },
                });
                this.tables = Array.isArray(data?.tables) ? data.tables : [];
                this.summaryMarkdown = data?.summary_markdown || "";
                this.databasesScanned = data?.databases_scanned || 0;
                this.databasesTotal = data?.databases_total || 0;
                if (!this.summaryMarkdown && !this.tables.length) {
                    this.errorMessage = "The catalog returned no tables for this scope.";
                }
            } catch (err) {
                const msg = err.response?.data?.error || err.response?.data?.message || err.message || "Request failed";
                this.errorMessage = typeof msg === "string" ? msg : JSON.stringify(msg);
                this.$store.dispatch("addToast", { type: "error", message: this.errorMessage });
            } finally {
                this.submitting = false;
            }
        },
        async scanFiles() {
            this.errorMessage = "";
            this.catalogError = "";
            this.summaryMarkdown = "";
            this.datasets = [];
            if (this.scanDisabled && !this.submitting) return;
            this.submitting = true;
            const bucketScope = `s3://${this.currentClient.cid}.${this.credentialFilter}@${this.bucketFilter}/${this.prefixFilter || ""}`
                .replace(/([^:]\/)\/+/g, "$1");
            const payload = {
                cid: this.currentClient.cid,
                service_name: "surface",
                namespace: this.credentialFilter,
                prompt: this.scanPrompt.trim(),
                bucket: bucketScope,
                recursive: true,
                enrich_with_catalog: this.enrichWithCatalog,
            };
            try {
                const url = `${this.agentsBase.replace(/\/$/, "")}/v1/discover-my-data/summary`;
                const { data } = await axios.post(url, payload, {
                    headers: {
                        Authorization: `Bearer ${this.accessToken}`,
                        "Content-Type": "application/json",
                    },
                });
                this.datasets = Array.isArray(data?.datasets) ? data.datasets : [];
                this.summaryMarkdown = data?.summary_markdown || "";
                if (!this.summaryMarkdown && !this.datasets.length) {
                    this.errorMessage = "No files were found for this scope.";
                }
            } catch (err) {
                const msg = err.response?.data?.error || err.response?.data?.message || err.message || "Request failed";
                this.errorMessage = typeof msg === "string" ? msg : JSON.stringify(msg);
                this.$store.dispatch("addToast", { type: "error", message: this.errorMessage });
            } finally {
                this.submitting = false;
            }
        },
    },
};
</script>

<style lang="scss" scoped>
@use '../../theme/scss/_user-variables.scss' as *;

/* ── Mode switch: catalog-first vs. file-browser-first ────────────────── */
.dmd-mode-switch {
    display: flex;
    align-items: center;
    gap: 0.5rem;
    flex-wrap: wrap;
}
.dmd-mode-btn {
    display: inline-flex;
    align-items: center;
    gap: 0.4rem;
    font-size: 0.82rem;
    font-weight: 600;
    padding: 0.4rem 0.9rem;
    border-radius: 0.5rem;
    border: 1px solid rgba(108, 117, 125, 0.25);
    background: var(--bs-body-bg, #fff);
    color: var(--bs-secondary, #6c757d);
    cursor: pointer;
    transition: all 0.15s;

    &:hover { border-color: var(--bs-primary, #2c7be5); }
    &.active {
        background: var(--bs-primary, #2c7be5);
        border-color: var(--bs-primary, #2c7be5);
        color: #fff;
    }
}
.dmd-mode-hint {
    font-size: 0.76rem;
    color: var(--bs-secondary, #6c757d);
    opacity: 0.85;
}

/* ── Toolbar: pill pickers + primary action, single slim row ─────────── */
.dmd-toolbar-card {
    border-radius: 0.625rem;
}
.dmd-toolbar {
    display: flex;
    align-items: center;
    gap: 0.6rem;
    flex-wrap: wrap;
    padding: 0.6rem 0.9rem;
}
.dmd-toolbar-spacer {
    flex: 1;
}
.dmd-pill {
    display: flex;
    align-items: center;
    gap: 0.5rem;
    padding: 0.3rem 0.75rem;
    border: 1px solid rgba(108, 117, 125, 0.25);
    border-radius: 0.5rem;
    background: var(--bs-body-bg, #fff);
    transition: border-color 0.15s;

    &:hover { border-color: var(--bs-primary, #2c7be5); }
    &:has(select:focus) { border-color: var(--bs-primary, #2c7be5); }

    .fa:first-child { font-size: 0.78rem; opacity: 0.55; flex-shrink: 0; }
    select {
        border: none;
        background: transparent;
        font-size: 0.85rem;
        padding: 0.1rem 0;
        min-width: 110px;
        max-width: 190px;
        color: var(--bs-body-color);
        &:focus { outline: none; box-shadow: none; }
    }
    .spinner-border {
        width: 0.7rem;
        height: 0.7rem;
        flex-shrink: 0;
    }
}
.dmd-pill--disabled {
    opacity: 0.55;
}
.dmd-pill-input {
    border: none;
    background: transparent;
    font-size: 0.85rem;
    padding: 0.1rem 0;
    min-width: 130px;
    max-width: 220px;
    color: var(--bs-body-color);
    &:focus { outline: none; box-shadow: none; }
}

/* ── Subfolder picker (browse button + dropdown menu on the prefix pill) ─ */
.dmd-pill--folder {
    position: relative;
}
.dmd-subfolder-toggle {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    border: none;
    background: transparent;
    padding: 0.1rem 0.2rem;
    color: var(--bs-secondary, #6c757d);
    flex-shrink: 0;

    .fa { font-size: 0.7rem; }
    .spinner-border { width: 0.65rem; height: 0.65rem; }
    &:hover:not(:disabled) { color: var(--bs-primary, #2c7be5); }
    &:disabled { opacity: 0.5; cursor: not-allowed; }
}
.dmd-subfolder-menu {
    position: absolute;
    top: calc(100% + 0.3rem);
    left: 0;
    z-index: 20;
    min-width: 220px;
    max-width: 320px;
    max-height: 260px;
    overflow-y: auto;
    background: var(--bs-body-bg, #fff);
    border: 1px solid rgba(108, 117, 125, 0.25);
    border-radius: 0.5rem;
    box-shadow: 0 0.5rem 1.5rem rgba(0, 0, 0, 0.12);
    padding: 0.3rem;
}
.dmd-subfolder-item {
    display: flex;
    align-items: center;
    width: 100%;
    text-align: left;
    font-size: 0.82rem;
    padding: 0.35rem 0.5rem;
    border: none;
    background: transparent;
    border-radius: 0.35rem;
    color: var(--bs-body-color);
    cursor: pointer;

    &:hover { background: rgba(44, 123, 229, 0.08); }
    .fa { opacity: 0.6; flex-shrink: 0; }
}
.dmd-subfolder-item--up {
    color: var(--bs-secondary, #6c757d);
    border-bottom: 1px solid rgba(108, 117, 125, 0.14);
    border-radius: 0;
    margin-bottom: 0.2rem;
    padding-bottom: 0.45rem;
}
.dmd-subfolder-msg {
    font-size: 0.8rem;
    padding: 0.4rem 0.5rem;
}
.dmd-hint-icon {
    cursor: help;
    opacity: 0.5;
    font-size: 0.85rem;
    transition: opacity 0.15s;
    &:hover { opacity: 0.9; }
}
.dmd-scan-btn {
    white-space: nowrap;
    padding: 0.4rem 1.2rem;
}

/* ── Hero empty state with quick-start suggestions ───────────────────── */
.dmd-hero {
    display: flex;
    flex-direction: column;
    align-items: center;
    text-align: center;
    padding: 4rem 1.5rem 3.5rem;
}
.dmd-hero-icon {
    width: 3.75rem;
    height: 3.75rem;
    border-radius: 50%;
    display: flex;
    align-items: center;
    justify-content: center;
    background: rgba(44, 123, 229, 0.12);
    color: #2c7be5;
    font-size: 1.5rem;
    margin-bottom: 1.25rem;
}
.dmd-hero-title {
    font-weight: 700;
    margin-bottom: 0.4rem;
}
.dmd-hero-subtitle {
    color: var(--bs-secondary, #6c757d);
    max-width: 32rem;
    margin-bottom: 1.75rem;
}
.dmd-hero-suggestions {
    display: flex;
    gap: 0.6rem;
    flex-wrap: wrap;
    justify-content: center;
}
.dmd-suggestion {
    display: inline-flex;
    align-items: center;
    gap: 0.5rem;
    font-size: 0.84rem;
    font-weight: 500;
    padding: 0.5rem 1.1rem;
    border-radius: 999px;
    border: 1px solid rgba(44, 123, 229, 0.25);
    background: rgba(44, 123, 229, 0.06);
    color: var(--bs-primary, #2c7be5);
    cursor: pointer;
    transition: all 0.15s;

    &:hover:not(:disabled) {
        background: rgba(44, 123, 229, 0.14);
        border-color: rgba(44, 123, 229, 0.4);
    }
    &:disabled {
        opacity: 0.5;
        cursor: not-allowed;
    }
    .fa { font-size: 0.78rem; opacity: 0.8; }
}

/* ── KPI summary strip ───────────────────────────────────────────────── */
.dmd-kpi-row {
    display: flex;
    gap: 0.75rem;
    flex-wrap: wrap;
}
.dmd-kpi {
    flex: 1;
    min-width: 150px;
    background: var(--bs-body-bg, #fff);
    border: 1px solid rgba(108, 117, 125, 0.14);
    border-radius: 0.5rem;
    padding: 0.65rem 1rem;
    display: flex;
    flex-direction: column;
    gap: 0.1rem;
}
.dmd-kpi-value {
    font-size: 1.5rem;
    font-weight: 700;
    line-height: 1.15;
    font-variant-numeric: tabular-nums;
    color: var(--bs-body-color);
}
.dmd-kpi-value-sub {
    font-size: 0.95rem;
    font-weight: 500;
    color: var(--bs-secondary, #6c757d);
}
.dmd-kpi-label {
    font-size: 0.68rem;
    font-weight: 600;
    text-transform: uppercase;
    letter-spacing: 0.04em;
    color: var(--bs-secondary, #6c757d);
}
.dmd-kpi--ok .dmd-kpi-value { color: #009f60; }
.dmd-kpi--muted .dmd-kpi-value { color: var(--bs-secondary, #6c757d); }
.dmd-kpi--warn {
    border-color: rgba(255, 193, 7, 0.4);
    background: rgba(255, 193, 7, 0.06);
    .dmd-kpi-value { color: #856404; }
}

/* ── Tables tab: filter chips + search ───────────────────────────────── */
.dmd-table-toolbar {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 1rem;
    flex-wrap: wrap;
}
.dmd-filter-chips {
    display: flex;
    gap: 0.4rem;
    flex-wrap: wrap;
}
.dmd-chip {
    display: inline-flex;
    align-items: center;
    gap: 0.35rem;
    font-size: 0.76rem;
    font-weight: 500;
    padding: 0.3rem 0.7rem;
    border-radius: 999px;
    border: 1px solid rgba(108, 117, 125, 0.25);
    background: var(--bs-body-bg, #fff);
    color: var(--bs-body-color);
    cursor: pointer;
    transition: all 0.15s;

    &:hover { border-color: var(--bs-primary, #2c7be5); }
    &.active {
        background: var(--bs-primary, #2c7be5);
        border-color: var(--bs-primary, #2c7be5);
        color: #fff;
        .dmd-chip-count { background: rgba(255, 255, 255, 0.25); color: #fff; }
    }
}
.dmd-chip-count {
    background: rgba(108, 117, 125, 0.14);
    color: var(--bs-secondary, #6c757d);
    border-radius: 999px;
    padding: 0.05rem 0.4rem;
    font-size: 0.68rem;
    font-variant-numeric: tabular-nums;
}
.dmd-table-search {
    position: relative;
    min-width: 220px;

    .fa-magnifying-glass {
        position: absolute;
        left: 0.65rem;
        top: 50%;
        transform: translateY(-50%);
        font-size: 0.75rem;
        color: var(--bs-secondary, #6c757d);
        opacity: 0.6;
        pointer-events: none;
    }
    input { padding-left: 1.8rem; }
}
.dmd-table-card {
    transition: box-shadow 0.15s;
}

/* ── Results area ────────────────────────────────────────────────────── */
.discover-card {
    border-radius: 0.5rem;
}

.discover-results-placeholder {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    text-align: center;
    min-height: 240px;
    padding: 2rem 1.5rem;
}

/* ── Summary markdown ────────────────────────────────────────────────── */
.discover-markdown {
    :deep(h2) { border-bottom: 1px solid #dee2e6; padding-bottom: 0.35rem; margin-bottom: 0.75rem; }
    :deep(h3) { padding: 0.4rem 0.75rem; background: #edf2f9; border-radius: 4px; margin-bottom: 0.5rem; }
    :deep(table td), :deep(table th) { padding: 0.5em; }
    :deep(pre) { background: #f8f9fa; border-radius: 6px; padding: 1rem; font-size: 0.8125rem; overflow-x: auto; }
    :deep(code):not(pre code) { background: rgba(44, 123, 229, 0.08); color: #2c7be5; border-radius: 3px; padding: 0.1em 0.3em; }
    /* s3:// paths get auto-linked by the markdown renderer but aren't navigable — style as
       inline code, not a hyperlink, so they don't read as clickable when they aren't. */
    :deep(a) {
        color: var(--bs-body-color);
        background: rgba(108, 117, 125, 0.08);
        text-decoration: none;
        font-family: var(--bs-font-monospace, monospace);
        font-size: 0.85em;
        padding: 0.05em 0.35em;
        border-radius: 3px;
        word-break: break-all;
    }
    :deep(a:hover) { text-decoration: underline; }
}

/* ── ZCR check cards (Ingestion / Lookup side by side) ──────────────── */
.zcr-check-card {
    height: 100%;
    padding: 0.6rem 0.75rem;
    background: rgba(108, 117, 125, 0.04);
    border: 1px solid rgba(108, 117, 125, 0.14);
    border-radius: 0.375rem;
}

/* ── Inspect (catalog identify -> credential-scoped file scan) ──────── */
.dmd-inspect-divider {
    border-top: 1px dashed rgba(108, 117, 125, 0.25);
}
.dmd-inspect-result {
    background: rgba(44, 123, 229, 0.03);
    border: 1px solid rgba(44, 123, 229, 0.14);
    border-radius: 0.375rem;
    padding: 0.75rem;
}
.dmd-file-list {
    display: flex;
    flex-direction: column;
    gap: 0.3rem;
}
.dmd-file-row {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 0.75rem;
    padding: 0.3rem 0.5rem;
    background: var(--bs-body-bg, #fff);
    border: 1px solid rgba(108, 117, 125, 0.12);
    border-radius: 4px;
}

/* ── Dataset / table badges ──────────────────────────────────────────── */
.ds-badge {
    display: inline-flex;
    align-items: center;
    font-size: 0.7rem;
    font-weight: 600;
    padding: 0.2rem 0.55rem;
    border-radius: 999px;
    white-space: nowrap;
}
.ds-badge--format { background: rgba(108, 117, 125, 0.12); color: #6c757d; }
.ds-badge--zcr-ok {
    background: rgba(0, 191, 115, 0.12);
    color: #009f60;
    border: 1px solid rgba(0, 159, 96, 0.3);
}
.ds-badge--zcr-warn {
    background: rgba(255, 193, 7, 0.12);
    color: #856404;
    border: 1px solid rgba(255, 193, 7, 0.35);
}
.ds-badge--type {
    background: rgba(44, 123, 229, 0.1);
    color: #2c7be5;
    border: 1px solid rgba(44, 123, 229, 0.2);
}
.ds-label {
    font-size: 0.65rem;
    font-weight: 600;
    letter-spacing: 0.05em;
    text-transform: uppercase;
    color: var(--bs-secondary, #6c757d);
    margin-bottom: 0.2rem;
}
.ds-location {
    font-size: 0.8rem;
    word-break: break-all;
    color: var(--bs-body-color);
    background: transparent;
    padding: 0;
}
.ds-col-badge {
    display: inline-block;
    font-size: 0.72rem;
    padding: 0.15rem 0.5rem;
    background: rgba(44, 123, 229, 0.06);
    border: 1px solid rgba(44, 123, 229, 0.18);
    border-radius: 4px;
    color: #2c5fa8;
    font-family: var(--bs-font-monospace, monospace);
}

/* ── Deploy section ──────────────────────────────────────────────────── */
.deploy-pipeline-name { font-size: 0.875rem; color: var(--bs-body-color); }
.deploy-config-tabs { display: flex; gap: 0.25rem; }
.deploy-config-tab {
    padding: 0.25rem 0.75rem;
    font-size: 0.78rem;
    font-weight: 500;
    border: 1px solid #d0d7e0;
    border-radius: 4px;
    background: #f8f9fa;
    color: #6c757d;
    cursor: pointer;
    transition: all 0.15s;
    &.active {
        background: var(--bs-primary, #2c7be5);
        border-color: var(--bs-primary, #2c7be5);
        color: #fff;
    }
}
.deploy-steps { display: flex; flex-direction: column; gap: 0.5rem; padding-left: 0.25rem; }
.deploy-step {
    display: flex;
    align-items: center;
    gap: 0.6rem;
    font-size: 0.8125rem;
    color: #adb5bd;
    &.active { color: var(--bs-primary, #2c7be5); }
    &.done   { color: #00bf73; }
}
.deploy-step__dot {
    width: 0.55rem;
    height: 0.55rem;
    border-radius: 50%;
    background: currentColor;
    flex-shrink: 0;
}
.deploy-step.active .deploy-step__dot {
    animation: pulse-dot 1s ease-in-out infinite;
}
@keyframes pulse-dot {
    0%, 100% { opacity: 1; transform: scale(1); }
    50%       { opacity: 0.4; transform: scale(0.7); }
}

/* ── Code block ──────────────────────────────────────────────────────── */
.discover-code-block {
    background: rgba(108, 117, 125, 0.06);
    border: 1px solid rgba(108, 117, 125, 0.18);
    border-radius: 0.375rem;
    padding: 0.875rem 1rem;
    font-size: 0.78rem;
    line-height: 1.6;
    overflow-x: auto;
    white-space: pre;
    max-height: 380px;
    overflow-y: auto;
}

/* ── Loading skeleton ────────────────────────────────────────────────── */
@keyframes shine {
    to { background-position-x: -200%; }
}
.loading-content-card {
    background: #fff;
    border-radius: 5px;
    .content { padding: 15px; }
    h2, p {
        background: linear-gradient(110deg, #ececec 8%, #f5f5f5 18%, #ececec 33%);
        border-radius: 5px;
        background-size: 200% 100%;
        animation: 1.5s shine linear infinite;
    }
    h2 { height: 30px; margin-bottom: 12px; }
    p  { height: 70px; }
}
.loading-content-card-dark {
    background: $dark-card-bg;
    border-radius: 5px;
    .content { padding: 15px; }
    h2, p {
        background: linear-gradient(110deg, $dark-elevated-bg 8%, $dark-border-color 18%, $dark-elevated-bg 33%);
        border-radius: 5px;
        background-size: 200% 100%;
        animation: 1.5s shine linear infinite;
    }
    h2 { height: 30px; margin-bottom: 12px; }
    p  { height: 70px; }
}
</style>

<style lang="scss">
@use '../../theme/scss/_user-variables.scss' as *;

body.dark-mode {
    .discover-markdown {
        h3 { background: $dark-elevated-bg; }
        pre { background: $dark-elevated-bg; }
        a { background: rgba(255,255,255,0.06); }
    }
    .zcr-check-card { background: rgba(255,255,255,0.03); border-color: $dark-border-color; }
    .dmd-inspect-divider { border-color: $dark-border-color; }
    .dmd-inspect-result { background: rgba(255,255,255,0.03); border-color: $dark-border-color; }
    .dmd-file-row { background: $dark-elevated-bg; border-color: $dark-border-color; }
    .discover-code-block { background: $dark-elevated-bg; border-color: $dark-border-color; }
    .deploy-config-tab { background: $dark-elevated-bg; border-color: $dark-border-color; color: #adb5bd; }
    .deploy-config-tab.active { background: var(--bs-primary, #2c7be5); border-color: var(--bs-primary, #2c7be5); color: #fff; }
    .dmd-kpi { background: $dark-elevated-bg; border-color: $dark-border-color; }
    .dmd-chip { background: $dark-elevated-bg; border-color: $dark-border-color; color: #adb5bd; }
    .dmd-chip-count { background: rgba(255,255,255,0.08); }
    .dmd-mode-btn { background: $dark-elevated-bg; border-color: $dark-border-color; color: #adb5bd; }
    .dmd-mode-btn.active { background: var(--bs-primary, #2c7be5); border-color: var(--bs-primary, #2c7be5); color: #fff; }
    .ds-col-badge { background: rgba(255,255,255,0.04); border-color: $dark-border-color; color: #8eb4f8; }
    .dmd-subfolder-menu { background: $dark-elevated-bg; border-color: $dark-border-color; }
    .dmd-subfolder-item:hover { background: rgba(255,255,255,0.06); }
    .dmd-subfolder-item--up { border-color: $dark-border-color; }
}
</style>
